/// <amd-module name="VSS/Diagnostics/Dialog/DiagnosticsGraph" />
import d3 = require("d3");
import React = require("react");
import { IVssComponentProperties, VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { ContributionLink, ContributionNode } from "VSS/Diagnostics/Dialog/DiagnosticsDialog.types";
import { ContributionQueryOptions } from "VSS/Platform/Contracts/Contributions";

export interface PerfDiagnosticsGraphProps extends IVssComponentProperties {
    contributions: ContributionNode[];
    relationships: ContributionLink[];
    displayedNavigation: string[];
}

export class PerfDiagnosticsGraph extends VssComponent<PerfDiagnosticsGraphProps> {
    constructor(props: PerfDiagnosticsGraphProps, context: IVssComponentContext) {
        super(props, context);
    }

    private static ColorMap: { [contributionType: string]: string } = {
        "ms.vss-web.site": "#001a66",
        "ms.vss-web.page": "#0033cc",
        "ms.vss-web.hub-groups-collection": "#0040ff",
        "ms.vss-web.hub-group": "#3366ff",
        "ms.vss-web.hub": "#668cff",
        "ms.vss-web.content": "#800000",
        "ms.vss-web.component": "#e60000",
        "ms.vss-web.data-provider": "#00b300",
        "ms.vss-web.route": "#996633"
    };

    private svgElement: SVGElement | null;

    public render(): JSX.Element {
        return (
            <svg
                className="canvas"
                ref={svgElement => {
                    this.svgElement = svgElement;
                }}
            />
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();

        if (this.svgElement === null) {
            return;
        }

        const { contributions, relationships, displayedNavigation } = this.props;

        // We want to start the graph with the elements from left to right.
        // The site on the left and the target nav element on the right.
        for (let contribution of contributions) {
            if (contribution.type === "ms.vss-web.site") {
                contribution.x = 20;
                contribution.y = this.svgElement.clientHeight / 2;
            } else {
                for (let navigationIndex = 0; navigationIndex < displayedNavigation.length; navigationIndex++) {
                    if (contribution.id === displayedNavigation[navigationIndex]) {
                        contribution.x = ((this.svgElement.clientWidth - 40) / displayedNavigation.length) * navigationIndex;
                        contribution.y = this.svgElement.clientHeight / 2;
                    }
                }

                if (!contribution.x || !contribution.y) {
                    contribution.x = Math.min(this.svgElement.clientWidth, Math.max(0, Math.random() * this.svgElement.clientWidth));
                    contribution.y = Math.min(this.svgElement.clientHeight, Math.max(0, Math.random() * this.svgElement.clientHeight));
                }
            }
        }

        // Attach to our svgElement to draw our graph.
        const svg = d3.select<SVGElement, {}>(this.svgElement);

        const simulation = d3
            .forceSimulation<ContributionNode>()
            .force(
                "link",
                d3.forceLink<ContributionNode, ContributionLink>().id(function(node) {
                    return node.id;
                })
            )
            .force("charge", d3.forceManyBody())
            .force("center", d3.forceCenter(this.svgElement.clientWidth / 2, this.svgElement.clientHeight / 2));

        // Create the set of relationships and contributions for the graph.
        const links = svg
            .append("g")
            .attr("class", "relationships")
            .selectAll("line")
            .data<ContributionLink>(relationships)
            .enter()
            .append("line")
            .attr("stroke", this.getRelationshipColor)
            .attr("stroke-width", this.getRelationshipSize);
        const nodes = svg
            .append("g")
            .attr("class", "contributions")
            .selectAll("circle")
            .data<ContributionNode>(contributions)
            .enter()
            .append("circle")
            .attr("r", this.getContributionSize)
            .attr("fill", this.getContributionColor)
            .call(
                d3
                    .drag<Element, ContributionNode>()
                    .on("start", function(contribution: ContributionNode) {
                        if (!d3.event.active) {
                            simulation.alphaTarget(0.5).restart();
                        }
                    })
                    .on("drag", function(contribution: ContributionNode) {
                        contribution.fx = d3.event.x;
                        contribution.fy = d3.event.y;
                    })
                    .on("end", function(contribution: ContributionNode) {
                        if (!d3.event.active) {
                            simulation.alphaTarget(0);
                        }
                    })
            );

        // Give each node a textual title.
        nodes.append("title").text(function(contribution: ContributionNode) {
            return contribution.id;
        });

        const width = this.svgElement.clientWidth;
        const height = this.svgElement.clientHeight;
        simulation.nodes(contributions).on("tick", function() {
            // Ensure the nodes dont move outside the graph.
            for (let contribution of contributions) {
                contribution.x = Math.min(Math.max(5, contribution.x || 5), width - 5);
                contribution.y = Math.min(Math.max(5, contribution.y || 5), height - 5);
            }

            links
                .attr("x1", function(relationship: ContributionLink) {
                    return (relationship.source as ContributionNode).x || 0;
                })
                .attr("y1", function(relationship: ContributionLink) {
                    return (relationship.source as ContributionNode).y || 0;
                })
                .attr("x2", function(relationship: ContributionLink) {
                    return (relationship.target as ContributionNode).x || 0;
                })
                .attr("y2", function(relationship: ContributionLink) {
                    return (relationship.target as ContributionNode).y || 0;
                });
            nodes
                .attr("cx", function(contribution: ContributionNode) {
                    return contribution.x || 0;
                })
                .attr("cy", function(contribution: ContributionNode) {
                    return contribution.y || 0;
                });
        });

        simulation.force<d3.ForceLink<ContributionNode, ContributionLink>>("link")!.links(relationships);
    }

    private getContributionColor = (contribution: ContributionNode): string => {
        let color = "#888";
        if (PerfDiagnosticsGraph.ColorMap[contribution.type]) {
            color = PerfDiagnosticsGraph.ColorMap[contribution.type];
        }
        return color;
    };

    private getContributionSize = (contribution: ContributionNode): number => {
        if (contribution.type === "ms.vss-web.site") {
            return 10;
        }
        return contribution.size;
    };

    private getRelationshipColor = (relationship: ContributionLink): string => {
        let color = "#ACA";
        if ((relationship.outputOptions & ContributionQueryOptions.IncludeSelf) === ContributionQueryOptions.None) {
            color = "#DDD";
        }
        return color;
    };

    private getRelationshipSize = (relationship: ContributionLink): number => {
        if ((relationship.outputOptions & ContributionQueryOptions.IncludeSelf) === ContributionQueryOptions.None) {
            return 1;
        }
        return 1.5;
    };
}
